import React, { useEffect, useState } from 'react';

import { WeakPasswordRegex } from 'src/lib/graphqlTypes/serverErrors';

interface PasswordStatus {
  isWarning: boolean;
  isSuccess: boolean;
  errorMessage?: string;
  status: React.ReactNode;
}

type PasswordChecker = (password: string) => PasswordStatus;

export function checkServerErrorForPasswordFailure(
  serverError?: string,
): PasswordStatus {
  const passwordError = serverError?.match(WeakPasswordRegex)?.groups?.errorMsg;

  return passwordError
    ? {
        isWarning: true,
        isSuccess: false,
        errorMessage: passwordError,
        status: <>{passwordError}</>,
      }
    : {
        isWarning: false,
        isSuccess: true,
        status: null,
      };
}

const passwordLengthChecker: PasswordChecker = (password: string) => {
  if (password.length < 8) {
    return {
      isWarning: true,
      isSuccess: false,
      errorMessage:
        'Your password is too short and must be at least 8 characters.',
      status: (
        <>Your password is too short and must be at least 8 characters.</>
      ),
    };
  }

  return {
    isWarning: false,
    isSuccess: true,
    status: null,
  };
};

/**
 * This is a simple password length checker. The actual password checker was
 * moved to the server and results are returned via GraphQL.
 */
export function usePasswordChecker(): PasswordChecker {
  const [passwordChecker, setPasswordChecker] = useState<PasswordChecker>(
    () => passwordLengthChecker,
  );

  useEffect(() => {
    setPasswordChecker(() => passwordLengthChecker);
  }, [setPasswordChecker]);

  return React.useMemo(() => passwordChecker, [passwordChecker]);
}
